/**
 * Plan setup — the page before the grid (RP-1.1, RP-1.2, RP-1.3, RP-4.4).
 *
 * Confirmed with the product owner: this comes BEFORE the grid, because what is set here
 * decides how many columns the grid has, how hours are entered, and what the margin is
 * measured against.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THE SHAPE OF THIS PAGE
 * ──────────────────────────────────────────────────────────────────────────────
 * Four numbered steps down the left, one live summary rail on the right. The steps are
 * numbered because they are genuinely sequential — the Solution fixes the Account and the
 * tier, the tier fixes the Rate Basis, the cadence fixes how many columns there are and
 * whether a fee is even a question. Presenting them as four equal cards hid that.
 *
 * The rail answers "what am I about to get?" while you type: which rate column applies,
 * what the floor is, how many months the grid will have, and — since RP-4.4 — whether
 * hours are one figure or a column per month. Everything in it is derived from the form,
 * so there is nothing to keep in sync.
 *
 * RP-D2, settled: **[ACCT] cannot create an Account from this form.** The Account must
 * already exist — plans attach to Accounts, they do not create them. Creating a *Pending
 * Solution* on an owned Account is a different thing and is permitted
 * (`createPendingSolution`, RP-D1b).
 *
 * Only the fields a plan cannot start without are on screen by default. Commercial terms
 * — contingency, notes — sit behind a disclosure, because most plans do not set them and
 * a form that asks everything up front reads as a form that needs everything.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { CalendarRange, Check, ChevronDown, ExternalLink, Info } from "lucide-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { PlanDealOption } from "@/api/plan-deal-options";
import { DealSolutionCreate } from "@/components/plans/DealSolutionCreate";
import { MonthSelect } from "@/components/shared/MonthSelect";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useSolutionPlanRateCard } from "@/hooks/use-account-rate-overrides";
import { useSolutionTierIds } from "@/hooks/use-dashboard-solutions";
import { usePlanDealOptions } from "@/hooks/use-plan-deal-options";
import {
  type RefreshedPlanDeal,
  useCreatePlanDealSolution,
  useRefreshPlanDeal,
} from "@/hooks/use-plan-deal-solution";
import { usePodShapes, useTiersWithDefaultShape } from "@/hooks/use-pod-shapes";
import { usePlanningConstants, useRateCard } from "@/hooks/use-rate-card";
import {
  type CreatePlanInput,
  useCreateResourcePlan,
  useImportSolutionEstimate,
} from "@/hooks/use-resource-plans";
import {
  useSolutionEstimateProjects,
  useSolutionEstimateSpan,
} from "@/hooks/use-solution-estimate-projects";
import { useSession } from "@/lib/auth-client";
import { currentMonth, endMonthOptions, monthLabel, startMonthOptions } from "@/lib/month-options";
import { canSeeSettingsTab } from "@/lib/nav-access";
import type { SelectOption } from "@/lib/option-search";
import {
  applyDealDefaults,
  type DealDefaultFields,
  NO_DEAL_FILLED,
} from "@/lib/plan-deal-defaults";
import { anchoredEndMonth } from "@/lib/solution-estimate/anchor";
import { cn } from "@/lib/utils";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const DECIMAL2 = /^\d+(\.\d{1,2})?$/;

const schema = z
  .object({
    /** Every new plan is for a NOS Deal; its CRM solution comes from the Deal. */
    nymblDealId: z.string().min(1, { message: "Pick the NOS Deal this plan is for" }),
    solutionId: z.string().min(1, {
      message: "This Deal has no CRM solution yet — create one below, or pick another Deal.",
    }),
    estimateProjectId: z.string().optional(),
    name: z.string().trim().min(1, { message: "Give the plan a name" }).max(120),
    /** "" is "decide later" — the plan starts with an empty grid and a Custom Pod. */
    podShapeId: z.string().optional(),
    pricingModel: z.enum(["TimeAndMaterials", "FixedPrice"]),
    budgetCadence: z.enum(["Monthly", "Total"]),
    rateMode: z.enum(["Card", "ClientDictated"]),
    /**
     * Unset until the planner picks one — the Account's Default Rate Card applies until
     * then, so switching Solutions follows each Account's default (see `rateSource` below).
     */
    rateSource: z.enum(["account", "standard"]).optional(),
    startMonth: z.string().regex(MONTH, { message: "Use YYYY-MM" }),
    endMonth: z.string().regex(MONTH, { message: "Use YYYY-MM" }).or(z.literal("")).optional(),
    estimatedHours: z.string().regex(DECIMAL2).or(z.literal("")).optional(),
    contractedFee: z.string().regex(DECIMAL2).or(z.literal("")).optional(),
    contractedFeeReference: z.string().trim().max(120).optional(),
    contingencyPercent: z.string().regex(DECIMAL2).or(z.literal("")).optional(),
    commercialNotes: z.string().trim().max(4000).optional(),
  })
  // With an estimate picked the End Month is not asked — it comes from the estimate's
  // length (see `anchoredEndMonth`) and is filled in at submit.
  .refine(
    (v) =>
      v.budgetCadence !== "Total" ||
      (v.endMonth ?? "") !== "" ||
      (v.estimateProjectId ?? "") !== "",
    {
      message: "A Total-cadence plan needs an end month",
      path: ["endMonth"],
    },
  )
  .refine((v) => (v.endMonth ?? "") === "" || (v.endMonth ?? "") >= v.startMonth, {
    message: "The end month cannot precede the start month",
    path: ["endMonth"],
  })
  // The fee is forbidden only on T&M × Monthly — a run-rate retainer has no ceiling to
  // compare against. On T&M × Total it IS the not-to-exceed envelope (§4.1).
  .refine(
    (v) =>
      (v.contractedFee ?? "") === "" ||
      !(v.pricingModel === "TimeAndMaterials" && v.budgetCadence === "Monthly"),
    {
      message: "A Monthly T&M plan is a run-rate with no fee. Use a Total cadence for a ceiling.",
      path: ["contractedFee"],
    },
  );

type FormValues = z.infer<typeof schema>;

interface Props {
  /**
   * Every Solution this actor can plan, alphabetical — no longer a picker of its own: a
   * picked NOS Deal's Solution is looked up here for its name and Account. Never truncate.
   */
  solutions: readonly SelectOption[];
  solutionsLoading: boolean;
  onCreated: (planId: string) => void;
  onCancel: () => void;
}

/**
 * Radix `Select` cannot hold an empty-string value, so "no Shape" needs a sentinel of its
 * own; the form field itself still stores "".
 */
const SHAPE_NONE = "__none__";

/** `"DM · SA · CS"` — what a Shape will actually put in the grid. */
function shapeDisciplines(shape: {
  disciplineR1Code: string;
  disciplineR2Code: string | null;
  disciplineR3Code: string | null;
  disciplineR4Code: string | null;
}): string {
  return [
    shape.disciplineR1Code,
    shape.disciplineR2Code,
    shape.disciplineR3Code,
    shape.disciplineR4Code,
  ]
    .filter((code): code is string => code !== null)
    .join(" · ");
}

/** Inclusive month count, for the summary rail. */
function monthSpan(start: string, end: string): number {
  if (!MONTH.test(start) || !MONTH.test(end)) return 0;
  const [sy, sm] = start.split("-").map(Number);
  const [ey, em] = end.split("-").map(Number);
  if (sy === undefined || sm === undefined || ey === undefined || em === undefined) return 0;
  return Math.max(0, (ey - sy) * 12 + (em - sm) + 1);
}

export function PlanSetupForm({ solutions, solutionsLoading, onCreated, onCancel }: Props) {
  const create = useCreateResourcePlan();
  const importEstimate = useImportSolutionEstimate();
  const { data: card } = useRateCard();
  // SA does not see the Rate Card tab (2026-10-20), so the shortcut to it is hidden too.
  const { data: session } = useSession();
  const canSeeRateCard = canSeeSettingsTab(session?.user?.role, "/dashboard/settings/rate-card");
  const { data: constants } = usePlanningConstants();
  const { data: shapes } = usePodShapes();
  const { data: tiers } = useTiersWithDefaultShape();
  const { data: tierBySolution } = useSolutionTierIds();
  const [showTerms, setShowTerms] = useState(false);
  /**
   * The last name this form filled in for the user.
   *
   * Picking a Solution names the plan after it, but that must never overwrite a name they
   * typed. `formState.isDirty` cannot answer "did a human type this" — `setValue` marks the
   * field dirty itself — so the test is whether the field still holds exactly what we last
   * put there.
   */
  const autoName = useRef("");
  /** Same guard as `autoName`, for the Shape the tier implies. */
  const autoShape = useRef("");
  /**
   * What the linked NOS Deal last filled in — the same never-clobber guard, for the
   * CRM solution, Contracted Fee and Source reference a Deal defaults
   * (src/lib/plan-deal-defaults.ts).
   */
  const dealFilled = useRef<DealDefaultFields>(NO_DEAL_FILLED);
  const deals = usePlanDealOptions();
  /**
   * The Deal as it was when picked. Held here rather than looked up from the query at
   * submit: a refetch can drop a Deal that just closed, and the link must not vanish
   * silently with the fee it filled still in place — the server says so instead.
   */
  const [linkedDeal, setLinkedDeal] = useState<PlanDealOption | undefined>(undefined);
  /**
   * Every pick re-reads the Deal from NOS and brings its Solution into Staff
   * (src/api/plan-deal-solution.ts). An answer for a Deal that is no longer the one
   * picked is dropped, so switching Deals quickly can never show the previous Deal's
   * Solution (spec AC2).
   */
  const refreshDeal = useRefreshPlanDeal();
  const createDealSolution = useCreatePlanDealSolution();
  const pickedDealId = useRef("");

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      nymblDealId: "",
      solutionId: "",
      estimateProjectId: "",
      name: "",
      podShapeId: "",
      pricingModel: "TimeAndMaterials",
      budgetCadence: "Total",
      rateMode: "Card",
      rateSource: undefined,
      startMonth: currentMonth(),
      endMonth: "",
      estimatedHours: "",
      contractedFee: "",
      contractedFeeReference: "",
      contingencyPercent: "",
      commercialNotes: "",
    },
  });

  const values = form.watch();
  const solution = solutions.find((s) => s.value === values.solutionId);
  const monthly = values.budgetCadence === "Monthly";
  const feeAllowed = !(values.pricingModel === "TimeAndMaterials" && monthly);

  const estimateProjects = useSolutionEstimateProjects(Boolean(values.solutionId));

  /*
   * With an estimate picked, the planner chooses only the Start Month: the estimate's first
   * month lands on it, and the estimate's length fixes the End Month. The Solution API's
   * own month labels are the estimator's drafting calendar, not the engagement's dates —
   * using them as-is is what made an imported plan's months disagree with its start.
   */
  const usingEstimate = (values.estimateProjectId ?? "") !== "";
  const estimateSpan = useSolutionEstimateSpan(values.estimateProjectId ?? "");
  const estimateEnd =
    usingEstimate && estimateSpan.data
      ? anchoredEndMonth(values.startMonth, estimateSpan.data.monthCount)
      : null;
  /** The End Month this plan will actually be created with — "" for none (yet). */
  const effectiveEnd = usingEstimate ? (estimateEnd ?? "") : (values.endMonth ?? "");

  /*
   * Which Rate Card this plan will price off. Always offered in Card mode — the Standard
   * Rate Card or the Account Rate Card — starting on the Account's Default Rate Card (set
   * on the Account page) until the planner picks. With no negotiated rates the two price
   * identically, and the plan's custom rates become the Account's Rate Card when it is
   * Won either way (src/api/account-rate-card-seed.ts).
   */
  const accountCard = useSolutionPlanRateCard(values.solutionId);
  const accountCardData = accountCard.data;
  const accountHasCard = (accountCardData?.negotiatedRateCount ?? 0) > 0;
  const defaultRateSource = accountCardData?.defaultRateSource === "List" ? "standard" : "account";
  const rateSource = values.rateSource ?? defaultRateSource;
  const priceOffAccountCard = rateSource === "account";

  /**
   * The Planning Horizon (RP-14.1) — how far a Monthly plan with no end month projects.
   * Read from Configuration rather than assumed, so the rail cannot promise a different
   * number of columns from the one the server builds.
   */
  const horizonRaw = constants?.find((c) => c.name === "planningHorizonMonths")?.value;
  const horizon = horizonRaw === undefined ? 12 : Math.max(1, Math.trunc(Number(horizonRaw)));

  const activeShapes = (shapes ?? []).filter((shape) => shape.active);
  const pickedShapeRow = activeShapes.find((shape) => shape.id === values.podShapeId);
  const pickedShape =
    pickedShapeRow === undefined
      ? undefined
      : {
          name: pickedShapeRow.name,
          seatCount: shapeDisciplines(pickedShapeRow).split(" · ").length,
        };
  /**
   * The Shape this Solution's Engagement Standard Tier defaults to — the same precedence
   * `listShapesForPlan` uses once a plan exists, so the form pre-selects what the plan
   * screen would have recommended anyway.
   */
  const tierDefaultShapeId = (solutionId: string): string => {
    const tierId = tierBySolution?.get(solutionId) ?? null;
    if (tierId === null) return "";
    return tiers?.find((t) => t.id === tierId)?.defaultShapeId ?? "";
  };

  /*
   * Start offers a window around today rather than only the future: a plan is sometimes
   * written up after the work has started, and a picker that refuses to say so would send
   * the planner looking for a way round it.
   */
  const startOptions = startMonthOptions(values.startMonth);
  /** Counted from the chosen start — not from today, which could precede it. */
  const endOptions = endMonthOptions(values.startMonth, values.endMonth);

  const columns =
    effectiveEnd !== "" ? monthSpan(values.startMonth, effectiveEnd) : monthly ? horizon : null;

  /**
   * Set the CRM solution — always the picked NOS Deal's — and everything that follows
   * from it: the default plan name and Pod Shape, and a fresh Solution estimate choice.
   */
  function applySolution(next: string, options: readonly SelectOption[] = solutions) {
    form.setValue("solutionId", next, { shouldValidate: next !== "" });
    // The estimate picker only shows once a Solution is picked — a previously chosen
    // project is stale once that changes.
    form.setValue("estimateProjectId", "");
    const picked = options.find((s) => s.value === next);
    if (picked === undefined) {
      // Cleared (a linked Deal had no CRM solution): drop the name and Shape this form
      // derived from the previous one, keeping anything the planner chose themselves.
      if (form.getValues("name") === autoName.current) form.setValue("name", "");
      if ((form.getValues("podShapeId") ?? "") === autoShape.current) {
        form.setValue("podShapeId", "");
      }
      autoName.current = "";
      autoShape.current = "";
      return;
    }
    const typed = form.getValues("name");
    if (typed === "" || typed === autoName.current) {
      form.setValue("name", picked.label, { shouldValidate: true });
      autoName.current = picked.label;
    }
    // Same never-clobber rule as the name: only move a Shape this form put there, never
    // one the planner chose.
    const chosenShape = form.getValues("podShapeId") ?? "";
    if (chosenShape === "" || chosenShape === autoShape.current) {
      const suggested = tierDefaultShapeId(next);
      form.setValue("podShapeId", suggested);
      autoShape.current = suggested;
    }
  }

  /** Pick a NOS Deal: its CRM solution, and re-populate the fee fields it defaults. Applied
   *  at once from the list as loaded, then again once NOS has been re-read. */
  function applyDeal(dealId: string) {
    form.setValue("nymblDealId", dealId, { shouldValidate: true });
    const deal = dealId === "" ? null : (deals.data?.byId.get(dealId) ?? null);
    applyDealFields(deal, solutions);
    pickedDealId.current = dealId;
    if (dealId !== "") refreshPickedDeal(dealId);
  }

  function refreshPickedDeal(dealId: string) {
    refreshDeal.mutate(dealId, { onSuccess: (fresh) => applyFreshDeal(dealId, fresh) });
  }

  /** The Deal as NOS has it now, with the Solution list reloaded after any import. */
  function applyFreshDeal(dealId: string, fresh: RefreshedPlanDeal) {
    if (pickedDealId.current !== dealId) return;
    applyDealFields(fresh.deal, fresh.solutionOptions);
  }

  function createSolutionForDeal(name: string) {
    const dealId = pickedDealId.current;
    if (dealId === "") return;
    createDealSolution.mutate(
      { dealId, name },
      { onSuccess: (fresh) => applyFreshDeal(dealId, fresh) },
    );
  }

  function applyDealFields(deal: PlanDealOption | null, options: readonly SelectOption[]) {
    setLinkedDeal(deal ?? undefined);
    const current = form.getValues();
    const { next, filled } = applyDealDefaults({
      current: {
        solutionId: current.solutionId,
        contractedFee: current.contractedFee ?? "",
        contractedFeeReference: current.contractedFeeReference ?? "",
      },
      filled: dealFilled.current,
      deal,
      visibleSolutionIds: new Set(options.map((s) => s.value)),
    });
    dealFilled.current = filled;
    if (next.solutionId !== current.solutionId) applySolution(next.solutionId, options);
    form.setValue("contractedFee", next.contractedFee, { shouldValidate: true });
    form.setValue("contractedFeeReference", next.contractedFeeReference, {
      shouldValidate: true,
    });
  }

  const feeFromDeal =
    linkedDeal !== undefined &&
    linkedDeal.amount !== null &&
    (values.contractedFee ?? "") === linkedDeal.amount;

  function onSubmit(input: FormValues) {
    const blank = (v: string | undefined) => (v === undefined || v === "" ? null : v);
    // The End Month comes from the estimate, so the plan cannot be created until its
    // length is known — creating it first would build the grid on the wrong span.
    if (usingEstimate && estimateEnd === null) {
      form.setError("estimateProjectId", {
        message: estimateSpan.isError
          ? "Couldn't read this Solution estimate's months. Try again, or start from a blank grid."
          : estimateSpan.isLoading
            ? "Still reading this Solution estimate's months — try again in a moment."
            : "This Solution estimate has no months to import yet.",
      });
      return;
    }
    const payload: CreatePlanInput = {
      solutionId: input.solutionId,
      name: input.name,
      // Omitted entirely when unset — the server treats an absent shape as "no Pod yet",
      // which is not the same as composing a Custom one.
      ...((input.podShapeId ?? "") === "" ? {} : { podShapeId: input.podShapeId }),
      pricingModel: input.pricingModel,
      budgetCadence: input.budgetCadence,
      rateMode: input.rateMode,
      // Sent only when the form actually showed a choice: the planner's pick, or the
      // Account's Default Rate Card once it has loaded. Otherwise omitted, so the server
      // applies the Account's default rather than a guess made while it was loading.
      // Client-Dictated plans keep the old `true`.
      ...(input.rateMode !== "Card"
        ? { useAccountRateCard: true }
        : input.rateSource !== undefined || accountCardData !== undefined
          ? { useAccountRateCard: rateSource === "account" }
          : {}),
      startMonth: input.startMonth,
      endMonth: usingEstimate ? estimateEnd : blank(input.endMonth),
      estimatedHours: blank(input.estimatedHours),
      contractedFee: feeAllowed ? blank(input.contractedFee) : null,
      // RP-1.2: the basis always matches the cadence.
      contractedFeeBasis:
        feeAllowed && blank(input.contractedFee) !== null ? input.budgetCadence : null,
      // Hidden (and so not the planner's to see) when there is no fee to reference.
      contractedFeeReference: feeAllowed ? blank(input.contractedFeeReference) : null,
      // The server derives the plan's Solution from this Deal.
      nymblDealId: input.nymblDealId,
      contingencyPercent: blank(input.contingencyPercent),
      commercialNotes: blank(input.commercialNotes),
    };
    // RP-4.4: MonthByMonth is always safe here, regardless of budgetCadence — only
    // SteadyState is restricted to a Monthly cadence — and an import needs a column per
    // month to land the estimate's hours-by-month shape onto.
    if (blank(input.estimateProjectId) !== null) {
      payload.entryMode = "MonthByMonth";
    }
    create.mutate(payload, {
      onSuccess: (plan) => {
        const estimateProjectId = blank(input.estimateProjectId);
        if (estimateProjectId !== null) {
          importEstimate.mutate(
            { planId: plan.id, projectId: estimateProjectId },
            { onSettled: () => onCreated(plan.id) },
          );
        } else {
          onCreated(plan.id);
        }
      },
    });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_296px]"
      >
        <div className="space-y-8">
          {/* ── 1 ── */}
          <Step n={1} title="What is being planned">
            <FormField
              control={form.control}
              name="nymblDealId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>NOS Deal</FormLabel>
                  <FormControl>
                    <SearchableSelect
                      options={deals.data?.options ?? []}
                      value={field.value}
                      onValueChange={applyDeal}
                      // The CRM solution is looked up in `solutions` on pick, so wait for both.
                      loading={deals.isLoading || solutionsLoading}
                      placeholder="Pick the NOS Deal this plan is for"
                      searchPlaceholder="Search by deal or Account…"
                      emptyMessage="No open NOS Deal matches that."
                    />
                  </FormControl>
                  <FormDescription>
                    {deals.isError
                      ? "Couldn’t load NOS Deals right now, so a plan can’t be created yet. Try again in a moment."
                      : "Sets the CRM solution, and fills in the Contracted Fee from the Deal’s amount (you can change the fee)."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="solutionId"
              render={() => (
                <FormItem>
                  <FormLabel>CRM solution</FormLabel>
                  <p className="text-sm">
                    {solution !== undefined ? (
                      <>
                        {solution.label}
                        {solution.group ? (
                          <span className="text-muted-foreground"> · {solution.group}</span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-muted-foreground">
                        {linkedDeal === undefined
                          ? "Comes from the NOS Deal."
                          : refreshDeal.isPending
                            ? "Checking NOS for this Deal’s Solution…"
                            : linkedDeal.nymblSolutionId === null
                              ? "This Deal has no CRM solution yet."
                              : linkedDeal.solutionId === null
                                ? "This Deal’s Solution is in NOS but couldn’t be brought into Staff — ask an Admin to import it."
                                : "This Deal’s CRM solution isn’t one you can plan."}
                      </span>
                    )}
                  </p>
                  {linkedDeal !== undefined && solution !== undefined && refreshDeal.isPending ? (
                    <p className="text-xs text-muted-foreground">
                      Checking NOS for changes to this Solution…
                    </p>
                  ) : null}
                  {linkedDeal !== undefined && refreshDeal.isError ? (
                    <p className="flex items-center gap-2 text-sm text-destructive">
                      Couldn’t check NOS for this Deal’s Solution.
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => refreshPickedDeal(linkedDeal.dealId)}
                      >
                        Retry
                      </Button>
                    </p>
                  ) : null}
                  {linkedDeal !== undefined &&
                  linkedDeal.nymblSolutionId === null &&
                  refreshDeal.isSuccess ? (
                    <DealSolutionCreate
                      key={linkedDeal.dealId}
                      deal={linkedDeal}
                      pending={createDealSolution.isPending}
                      onCreate={createSolutionForDeal}
                    />
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/*
              Kind (Base / Option / Change Order) used to sit beside this field, under a hint
              claiming a Solution's second plan "must say whether it is an Option or a Change
              Order". It does not: `resource_plan.kind` defaults to 'Base' in the column, the
              create schema has it optional and the handler falls back, and the one-Active-Plan
              rule is a partial unique index on `status = 'Active'` that never reads `kind`.
              So the control asked for a classification nothing enforced at creation time. The
              Change Order path that DOES turn on kind is cloning an existing Won or Staffed
              plan, which is a different screen and still there.
            */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={solution === undefined ? "e.g. TBOS RFP Budget" : solution.label}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Named after the CRM solution by default — type over it if this plan needs its
                    own name.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {values.solutionId ? (
              <FormField
                control={form.control}
                name="estimateProjectId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Solution estimate{" "}
                      <span className="font-normal text-muted-foreground">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <SearchableSelect
                        options={estimateProjects.data ?? []}
                        value={field.value ?? ""}
                        onValueChange={(next) => {
                          field.onChange(next);
                          form.clearErrors("estimateProjectId");
                          // The estimate now sets the End Month; a hand-picked one is moot.
                          form.setValue("endMonth", "", { shouldValidate: true });
                        }}
                        loading={estimateProjects.isLoading}
                        placeholder="No Solution estimate — start from a blank grid"
                        searchPlaceholder="Search by project or company…"
                        emptyMessage="No Solution estimate matches that search."
                      />
                    </FormControl>
                    <FormDescription>
                      Pulls that project&rsquo;s hours-by-role-by-month estimate onto this
                      plan&rsquo;s grid once it&rsquo;s created. Its first month lands on the Start
                      Month you pick below, and its length sets the End Month. Every Solution
                      estimate is listed, grouped by company &mdash; search for the one that matches
                      this engagement.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            {/*
              Pod Shape seeds the grid with one priced line per Pod seat, so the plan opens
              as the Pod you already know you are staffing rather than as a blank table you
              retype it into. Optional on purpose — a plan whose shape is not settled yet is
              a normal plan, not an incomplete one.
            */}
            <FormField
              control={form.control}
              name="podShapeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Pod Shape <span className="font-normal text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <Select
                    value={field.value === "" ? SHAPE_NONE : (field.value ?? SHAPE_NONE)}
                    onValueChange={(next) => {
                      field.onChange(next === SHAPE_NONE ? "" : next);
                      // A deliberate choice, so stop treating it as ours to overwrite.
                      autoShape.current = "";
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={SHAPE_NONE}>Decide later — start empty</SelectItem>
                      {activeShapes.map((shape) => (
                        <SelectItem key={shape.id} value={shape.id}>
                          {shape.name}
                          <span className="ml-1.5 text-muted-foreground">
                            {shapeDisciplines(shape)}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {field.value === "" || field.value === undefined
                      ? "The grid opens empty and you add lines as you go."
                      : "One Plan Line per Pod seat, at the card rate, with no hours yet."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Step>

          {/*
            ── 2 ──

            Three choices of the same kind, so they get the same shape: label, control, and a
            line that says what the choice means. They used to be a two-column grid with Rate
            Mode spanning underneath, which put Pricing Model (no description at all) beside
            Budget Cadence (two lines of it) — ragged bottoms, an L-shaped block, and a
            section that jumped every time a description swapped length under your cursor.
            The helper line is height-reserved for exactly that reason.
          */}
          <Step n={2} title="How it is sold">
            <FormField
              control={form.control}
              name="pricingModel"
              render={({ field }) => (
                <ChoiceRow
                  label="Pricing Model"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    ["TimeAndMaterials", "Time & Materials"],
                    ["FixedPrice", "Fixed Price"],
                  ]}
                  help={
                    field.value === "TimeAndMaterials"
                      ? "Billed for the hours actually worked."
                      : "A fixed fee for the scope, whatever the hours turn out to be."
                  }
                />
              )}
            />
            <FormField
              control={form.control}
              name="budgetCadence"
              render={({ field }) => (
                <ChoiceRow
                  label="Budget Cadence"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    ["Total", "Total"],
                    ["Monthly", "Monthly"],
                  ]}
                  help={
                    field.value === "Monthly"
                      ? "A run-rate. One hours-per-month figure per line."
                      : "An envelope with a start and an end. Hours entered month by month."
                  }
                />
              )}
            />
            <FormField
              control={form.control}
              name="rateMode"
              render={({ field }) => (
                <ChoiceRow
                  label="Rate Mode"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    ["Card", "Rate Card"],
                    ["ClientDictated", "Client-Dictated"],
                  ]}
                  help={
                    field.value === "Card"
                      ? "Lines take the bill rate for the resolved Rate Basis; an override needs a reason."
                      : "Each line carries its own bill rate; cost still comes from its Labor Category."
                  }
                />
              )}
            />
            {values.rateMode === "Card" && accountCardData !== undefined && (
              <FormField
                control={form.control}
                name="rateSource"
                render={({ field }) => (
                  <ChoiceRow
                    label="Rates"
                    value={rateSource}
                    onChange={field.onChange}
                    // The Account's Default Rate Card is marked, whichever is picked.
                    options={[
                      [
                        "account",
                        `Account Rate Card${defaultRateSource === "account" ? " (Default)" : ""}`,
                      ],
                      [
                        "standard",
                        `Standard Rate Card${defaultRateSource === "standard" ? " (Default)" : ""}`,
                      ],
                    ]}
                    help={
                      !accountHasCard
                        ? `${accountCardData.accountName} has no negotiated rates on ${accountCardData.rateBasisLabel} yet, so both start every line at the standard rate.`
                        : rateSource === "account"
                          ? `${accountCardData.accountName}'s ${accountCardData.negotiatedRateCount} negotiated rate${accountCardData.negotiatedRateCount === 1 ? " applies" : "s apply"} on ${accountCardData.rateBasisLabel}; every other Labor Category takes the standard rate.`
                          : `Ignores ${accountCardData.accountName}'s negotiated rates. Every line starts at the standard rate on ${accountCardData.rateBasisLabel}.`
                    }
                  />
                )}
              />
            )}
          </Step>

          {/* ── 3 ── */}
          <Step n={3} title="When it runs">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="startMonth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Month</FormLabel>
                    <FormControl>
                      <MonthSelect
                        value={field.value}
                        options={startOptions}
                        onValueChange={(next) => {
                          field.onChange(next);
                          // The End list is derived from Start, so a Start that moves past the
                          // chosen End would leave an end date before the beginning.
                          const end = form.getValues("endMonth") ?? "";
                          if (end !== "" && end < next) form.setValue("endMonth", "");
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      {usingEstimate
                        ? "The Solution estimate's first month lands here."
                        : "Defaults to this month; earlier months are there for backdating."}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {usingEstimate ? (
                <EstimateEndMonth
                  end={estimateEnd}
                  monthCount={estimateSpan.data?.monthCount ?? null}
                  loading={estimateSpan.isLoading}
                  failed={estimateSpan.isError}
                  onRetry={() => estimateSpan.refetch()}
                />
              ) : (
                <FormField
                  control={form.control}
                  name="endMonth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        End Month{" "}
                        {monthly && (
                          <span className="font-normal text-muted-foreground">(optional)</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <MonthSelect
                          value={field.value ?? ""}
                          options={endOptions}
                          onValueChange={field.onChange}
                          {...(monthly ? { noneLabel: "No end month" } : {})}
                        />
                      </FormControl>
                      <FormDescription>
                        {monthly
                          ? `"No end month" projects across the ${horizon}-month Planning Horizon.`
                          : "Required — it is what the envelope is measured over."}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </Step>

          {/* ── 4 ── */}
          <Step n={4} title="What sales agreed" optional>
            <FormField
              control={form.control}
              name="contractedFee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {monthly ? "Monthly" : "Total"} Contracted Fee
                    {feeAllowed && (
                      <span className="ml-1 font-normal text-muted-foreground">
                        {values.pricingModel === "FixedPrice"
                          ? "— required to reach Won"
                          : "— the not-to-exceed ceiling"}
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      inputMode="decimal"
                      placeholder={feeAllowed ? "378387.36" : "not applicable"}
                      disabled={!feeAllowed}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    {feeAllowed
                      ? feeFromDeal
                        ? `Filled from NOS Deal \u201c${linkedDeal.dealName}\u201d${monthly ? " \u2014 this is the Deal\u2019s total, check it against a monthly fee" : ""}.`
                        : "What was presented to the client; the variance against the plan's value is the discount."
                      : "Monthly T&M is a run-rate — switch the cadence to Total to record a ceiling."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {feeAllowed && (
              <FormField
                control={form.control}
                name="contractedFeeReference"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Source reference{" "}
                      <span className="font-normal text-muted-foreground">(optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Quote, SOW or CO number"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="estimatedHours"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Estimated hours{" "}
                    <span className="font-normal text-muted-foreground">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      inputMode="decimal"
                      placeholder="29660.00"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormDescription>
                    Becomes the plan's first estimate line on the Estimates tab — the plan is
                    checked against it, never driven by it.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Most plans set none of this, so it does not get to occupy the page. */}
            <button
              type="button"
              onClick={() => setShowTerms((v) => !v)}
              className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronDown
                className={cn("h-3 w-3 transition-transform", showTerms && "rotate-180")}
              />
              Commercial terms
            </button>
            {showTerms && (
              <div className="space-y-4 border-l-2 border-border pl-4">
                <FormField
                  control={form.control}
                  name="contingencyPercent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contingency %</FormLabel>
                      <FormControl>
                        <Input
                          inputMode="decimal"
                          placeholder="0.00"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="commercialNotes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea rows={3} {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormDescription>
                        Expenses, credits and payment terms are recorded here as notes only. USD.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </Step>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-5">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create plan & open grid"}
            </Button>
          </div>
        </div>

        {/* ── The live rail ── */}
        <aside className="space-y-3 lg:sticky lg:top-4">
          <Card title="Prices on">
            {values.rateMode === "ClientDictated" ? (
              <>
                <p className="font-display text-base font-bold leading-tight">Client-Dictated</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  No Rate Card lineage — a Rate Basis still resolves, so the margin floor applies.
                </p>
              </>
            ) : (
              <>
                <p className="font-display text-base font-bold leading-tight">
                  {solution === undefined
                    ? "Pick a CRM solution"
                    : accountCardData === undefined
                      ? "Resolved at creation"
                      : accountHasCard && priceOffAccountCard
                        ? `${accountCardData.accountName}'s Rate Card`
                        : "Standard Rate Card"}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {accountCardData === undefined
                    ? "From this CRM solution\u2019s Fixed-Staffed flag, else its Engagement Standard Tier."
                    : accountHasCard
                      ? `On ${accountCardData.rateBasisLabel}. ${priceOffAccountCard ? "Negotiated rates first, standard for the rest." : "The Account's negotiated rates are not used on this plan."}`
                      : `On ${accountCardData.rateBasisLabel}. ${accountCardData.accountName} has no Rate Card yet — custom rates on this plan are saved as its Rate Card when the plan is Won.`}
                </p>
              </>
            )}
            {card !== undefined && (
              <dl className="mt-2.5 space-y-1 border-t border-border pt-2.5 text-xs">
                <Row label="Margin floor" value={`${card.globalMarginFloorPercent}%`} />
                <Row
                  label="Amber band"
                  value={`${card.globalMarginAmberBandPoints} pts`}
                  note="above it"
                />
              </dl>
            )}
            {canSeeRateCard && (
              <Link
                to="/dashboard/settings/rate-card"
                className="mt-2.5 inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-2 hover:underline"
              >
                Open the Rate Card
                <ExternalLink className="h-2.5 w-2.5" />
              </Link>
            )}
          </Card>

          <Card title="The grid this builds">
            <dl className="space-y-1 text-xs">
              <Row
                label="Columns"
                value={
                  columns === null
                    ? usingEstimate
                      ? "from the Solution estimate"
                      : "set an end month"
                    : `${columns} month${columns === 1 ? "" : "s"}`
                }
              />
              <Row
                label="Span"
                value={
                  effectiveEnd === ""
                    ? `${values.startMonth} →`
                    : `${values.startMonth} → ${effectiveEnd}`
                }
              />
              <Row label="Sections" value="none — Workstreams optional" />
            </dl>
            <div
              className={cn(
                "mt-2.5 flex items-start gap-1.5 rounded border-l-2 px-2 py-1.5 text-xs leading-relaxed",
                monthly
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-border bg-muted/40 text-muted-foreground",
              )}
            >
              {monthly ? (
                <CalendarRange className="mt-px h-3 w-3 shrink-0 text-primary" />
              ) : (
                <Info className="mt-px h-3 w-3 shrink-0" />
              )}
              <span>
                {monthly ? (
                  <>
                    <span className="font-semibold">One figure per line.</span> Enter hours per
                    month once; the months are a read-only projection.
                  </>
                ) : (
                  <>
                    <span className="font-semibold">A column per month.</span> Hours are typed month
                    by month.
                  </>
                )}
              </span>
            </div>
          </Card>

          <Card title="Starts as">
            <ul className="space-y-1 text-xs text-muted-foreground">
              {[
                "Open — nothing counts as demand yet",
                "One default group; add Workstreams only if you want sections",
                // The rail must not keep promising an empty grid once a Shape is picked —
                // it is the one place that says what you are about to get.
                pickedShape === undefined
                  ? "No Plan Lines — add them inline in the grid"
                  : `${pickedShape.name} — ${pickedShape.seatCount} Plan Line${pickedShape.seatCount === 1 ? "" : "s"}, priced, no hours yet`,
              ].map((item) => (
                <li key={item} className="flex items-start gap-1.5">
                  <Check className="mt-0.5 h-2.5 w-2.5 shrink-0 text-success-700 dark:text-success-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        </aside>
      </form>
    </Form>
  );
}

// ── Small presentational pieces ───────────────────────────────────────────────

function Step({
  n,
  title,
  optional,
  children,
}: {
  n: number;
  title: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground tabular-nums">
          {n}
        </span>
        <h2 className="font-display text-sm font-semibold">{title}</h2>
        {optional === true && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Optional
          </span>
        )}
      </div>
      <div className="space-y-4 border-l border-border pl-7">{children}</div>
    </section>
  );
}

/**
 * A segmented control for a two-way choice.
 *
 * A `Select` for two options is a click too many, and the shape of the choice — this or
 * that — is worth showing rather than hiding behind a closed dropdown.
 */
function Choice(props: {
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
}) {
  return (
    // A group of toggle buttons rather than a div wearing role="radiogroup": that is what
    // it is, it needs no extra semantics, and `aria-pressed` is the correct state.
    <div className="inline-flex rounded-md border border-border p-0.5">
      {props.options.map(([value, label]) => (
        <button
          key={value}
          type="button"
          aria-pressed={props.value === value}
          onClick={() => props.onChange(value)}
          className={cn(
            "rounded px-3 py-1 text-sm font-medium transition-colors",
            props.value === value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * One "this or that" decision as a labeled row: label, segmented control, meaning.
 *
 * The helper line carries `min-h-8` so switching between two descriptions of different
 * lengths cannot reflow the step under the pointer that is still hovering the control.
 */
function ChoiceRow(props: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<readonly [string, string]>;
  help: string;
}) {
  return (
    <FormItem className="grid gap-x-4 gap-y-1.5 sm:grid-cols-[140px_minmax(0,1fr)]">
      <FormLabel className="sm:pt-1.5">{props.label}</FormLabel>
      <div className="min-w-0">
        <Choice value={props.value} onChange={props.onChange} options={props.options} />
        <FormDescription className="mt-1.5 min-h-8">{props.help}</FormDescription>
      </div>
      <FormMessage className="sm:col-start-2" />
    </FormItem>
  );
}

/**
 * The End Month when an estimate is picked: shown, never asked. It is the Start Month plus
 * the estimate's length, so the planner moves it by moving the Start Month.
 */
function EstimateEndMonth({
  end,
  monthCount,
  loading,
  failed,
  onRetry,
}: {
  end: string | null;
  monthCount: number | null;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="grid gap-2">
      <Label>End Month</Label>
      <div
        aria-live="polite"
        className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm"
      >
        {loading
          ? "Reading the Solution estimate…"
          : failed
            ? "Couldn't read the Solution estimate"
            : end === null
              ? "The Solution estimate has no months"
              : monthLabel(end)}
      </div>
      <p className="text-sm text-muted-foreground">
        {failed ? (
          <button
            type="button"
            onClick={onRetry}
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            Try again
          </button>
        ) : monthCount === null ? (
          "Set by the Solution estimate's length."
        ) : (
          `Set by the Solution estimate — ${monthCount} month${monthCount === 1 ? "" : "s"} from the Start Month.`
        )}
      </p>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-card p-3.5">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium tabular-nums">
        {value}
        {note !== undefined && (
          <span className="ml-1 font-normal text-muted-foreground">{note}</span>
        )}
      </dd>
    </div>
  );
}
